import { SEO } from '../../components/seo/SEO';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase, Post } from '../../lib/supabase';
import { getBaseUrl } from '../../lib/utils';
import { PostsPageHeader } from './components/PostsPageHeader';
import { PostsFilter } from './components/PostsFilter';
import { PostsPickup } from './components/PostsPickup';
import { PostsList } from './components/PostsList';

export default function PostsPage() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const { data, error } = await supabase
          .from('posts')
          .select('*')
          .lte('published_at', new Date().toISOString())
          .order('published_at', { ascending: false });
        if (error) throw error;
        setPosts(data || []);
      } catch {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const filteredCount = activeCategory === 'all'
    ? posts.length
    : posts.filter((p) => p.category_slug === activeCategory).length;

  const baseUrl = getBaseUrl();
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'つくってなんぼ — 記事一覧',
    url: `${baseUrl}/articles`,
    description: 'AIツール活用・UI再現・制作フロー・仕様整理などの実践記事一覧。',
  };

  return (
    <>
      <SEO
        title="記事一覧 | つくってなんぼ — AI実践メディア"
        description="AIツール活用、UI再現、制作フロー、仕様整理などの記事をまとめています。実際に作って詰まって直した過程を、再現できる形で整理した実践メディア。"
        canonical="/articles"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-[#FDFAF6]">
        {/* Page header */}
        <PostsPageHeader />

        {/* Pickup */}
        <PostsPickup posts={posts} />

        {/* Filter */}
        <PostsFilter
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
          totalCount={loading ? 0 : filteredCount}
        />

        {/* Articles list */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 pb-16">
          <PostsList
            posts={posts}
            activeCategory={activeCategory}
            loading={loading}
            onCategoryChange={setActiveCategory}
          />

          {/* Bottom nav */}
          {!loading && (
            <div className="mt-12 pt-8 border-t border-[#EDE7DF]">
              <p
                className="text-xs text-[#9A8E84] mb-4"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                他のページも見る
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/categories"
                  className="inline-flex items-center gap-1.5 text-sm text-[#7A6E62] hover:text-[#2C2416] transition-colors cursor-pointer"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  <i className="ri-grid-line text-[#5B7B6F]"></i>
                  カテゴリ一覧
                </Link>
                <span className="text-[#D4C9BE]">·</span>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-1.5 text-sm text-[#7A6E62] hover:text-[#2C2416] transition-colors cursor-pointer"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  <i className="ri-information-line text-[#5B7B6F]"></i>
                  About
                </Link>
                <span className="text-[#D4C9BE]">·</span>
                <Link
                  to="/search"
                  className="inline-flex items-center gap-1.5 text-sm text-[#7A6E62] hover:text-[#2C2416] transition-colors cursor-pointer"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  <i className="ri-search-line text-[#5B7B6F]"></i>
                  検索
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
